import { useId, useState } from 'react'

/**
 * Tenant indicator in the sidebar footer - renders `tnt:<tenant>`.
 *
 * Per UX-7, now closed on the MUR "Open Design Questions" page: keep + tooltip, to build muscle memory for
 * the M5 tenant switcher. The design handoff removes the chip from the header
 * bar, so it now sits in the sidebar footer beside the build strip. That keeps
 * the tenant on screen at all times without adding a control to the header the
 * handoff does not have.
 *
 * ## It states the claim, or it states nothing
 *
 * `tenant` used to default to `'suitsme'`, and `sidebar.tsx` rendered the chip
 * with no props - so it never read `claims.tnt` and asserted the M1 tenant as a
 * fact regardless of who was signed in. The MUR page "Information Architecture
 * and Navigation" calls this out directly: the chip "has to become claim-driven
 * before the Platform group ships. A tenant indicator that is wrong is worse
 * than no indicator, because staff will trust it."
 *
 * The default is therefore gone. `Sidebar` reads `claims.tnt` and passes it
 * down; when there is no claim there is no prop, and this component renders
 * **nothing**. Silence is honest where a hard-coded tenant name is not.
 *
 * It stays presentational - no `useAuth()` here - so it can be rendered in a
 * test or a story without an MSAL provider, and so there is exactly one place
 * (the sidebar) that decides what the tenant is.
 *
 * ## What it does not show
 *
 * The mockups put a credit union trading name in this slot ("Ardrossan Credit
 * Union"). That needs a directory mapping a `tnt` claim to a display name, and
 * no such lookup exists. The raw claim value is the honest intermediate - it is
 * the identifier the platform actually knows. When a directory lands, resolve
 * the name here and keep the identifier in the tooltip.
 *
 * Because it sits on `--side`, this is text on a dark ground rather than a
 * filled chip: a pale chip fill would fight the sidebar. The `tnt:` prefix
 * still means colour is never the only signal.
 *
 * Accessibility:
 *   - Focusable (`tabindex=0`) so keyboard users can reach the tooltip. It is
 *     not a button - nothing happens on activation.
 *   - The tooltip uses `role="tooltip"` and is referenced via
 *     `aria-describedby`, so screen readers announce it on focus.
 *   - The tooltip pops upward: the chip is at the bottom of the viewport.
 */
export interface TenantChipProps {
  /**
   * The `tnt` claim value. No default: absent means the signed-in user has no
   * tenant claim, and the chip renders nothing rather than guessing.
   */
  readonly tenant?: string | undefined
  /**
   * True for Murmuration staff, who work across tenants. Per the MUR IA page
   * they need a real switcher and every screen must say without ambiguity
   * which credit union they are looking at; until the switcher exists the
   * tooltip says so rather than implying a single tenant.
   */
  readonly canSwitch?: boolean
}

export function TenantChip({ tenant, canSwitch = false }: TenantChipProps): JSX.Element | null {
  const id = useId()
  const tooltipId = `${id}-tooltip`
  const [open, setOpen] = useState(false)

  if (tenant === undefined || tenant === '') return null

  const description = canSwitch
    ? `You are viewing the ${tenant} tenant. Switching tenants is not available yet.`
    : `You are viewing the ${tenant} tenant.`

  return (
    <span className="relative inline-flex">
      <span
        tabIndex={0}
        aria-describedby={tooltipId}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-murmuration-micro text-side-tx"
      >
        tnt:{tenant}
      </span>
      <span
        role="tooltip"
        id={tooltipId}
        className={
          open
            ? 'absolute bottom-full left-0 z-10 mb-murmuration-1 w-56 rounded-md bg-bg-surface px-murmuration-3 py-murmuration-2 text-murmuration-label text-text-secondary shadow-elev-2 ring-1 ring-border-default'
            : 'sr-only'
        }
      >
        {description}
      </span>
    </span>
  )
}
